/*
  Preloads the JotForm embeds used by the popup buttons on the landscape design page.

  Previously each button pointed featherlight at a hidden .popup-content div holding a
  <script src="form.jotform.com/jsform/ID"> embed. Featherlight defaults to persist:false,
  which deep-clones that div every time the popup opens — cloning an iframe re-fetches it
  from scratch, so the form loaded fresh on every single click.

  Instead: mount one iframe per distinct form directly on <body> once, in the background,
  and only show/hide it. The iframe is never cloned or re-parented after it is created,
  so it stays loaded and appears instantly.

  Markup contract: <div data-jotform="FORM_ID"></div> inside the existing
  .popup-content div that the button's data-featherlight attribute points at.
*/
(function () {
  'use strict';

  var forms = {}; // formId -> { title: String, modal: Element|null, iframe: Element|null }
  var openId = null;
  var lastFocused = null;

  function idle(fn) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 3000 });
    } else {
      setTimeout(fn, 200);
    }
  }

  function isJotformOrigin(origin) {
    try {
      var host = new URL(origin).hostname;
      return /(^|\.)jotform\.com$/.test(host) || /(^|\.)jotfor\.ms$/.test(host);
    } catch (e) {
      return false;
    }
  }

  /*
    Find every data-jotform marker, work back to the button that opens it, and take that
    button off featherlight. Runs as early as possible so there is no window where a click
    still reaches featherlight and opens an empty popup.
  */
  function claimTriggers() {
    var markers = document.querySelectorAll('[data-jotform]');

    Array.prototype.forEach.call(markers, function (marker) {
      var id = (marker.getAttribute('data-jotform') || '').trim();
      if (!/^\d+$/.test(id)) return;

      var host = marker.closest('.popup-content');
      if (!host || !host.id) return;

      var triggers = document.querySelectorAll('[data-featherlight="#' + host.id + '"]');
      if (!triggers.length) return;

      if (!forms[id]) {
        forms[id] = { title: '', modal: null, iframe: null };
      }

      Array.prototype.forEach.call(triggers, function (trigger) {
        if (!forms[id].title) {
          forms[id].title = (trigger.textContent || '').trim();
        }

        trigger.removeAttribute('data-featherlight');
        trigger.setAttribute('data-jotform-open', id);

        // These are <a> tags with no href, so they need help to stay keyboard operable.
        if (!trigger.hasAttribute('href')) {
          trigger.setAttribute('role', 'button');
          trigger.setAttribute('tabindex', '0');
        }
      });
    });

    return Object.keys(forms);
  }

  function build(id) {
    var entry = forms[id];
    if (!entry || entry.modal) return entry;

    var title = entry.title || 'Form';

    var modal = document.createElement('div');
    modal.className = 'jf-modal';
    modal.setAttribute('data-jotform-modal', id);

    var backdrop = document.createElement('div');
    backdrop.className = 'jf-modal__backdrop';
    backdrop.setAttribute('data-jotform-close', '');

    var dialog = document.createElement('div');
    dialog.className = 'jf-modal__dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', title);

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'jf-modal__close';
    close.setAttribute('data-jotform-close', '');
    close.setAttribute('aria-label', 'Close ' + title);
    close.innerHTML = '&#10005;';

    var body = document.createElement('div');
    body.className = 'jf-modal__body';

    var iframe = document.createElement('iframe');
    iframe.id = 'JotFormIFrame-' + id;
    iframe.title = title;
    iframe.src = 'https://form.jotform.com/' + id;
    iframe.allow = 'geolocation; microphone; camera; fullscreen; payment';
    iframe.setAttribute('allowtransparency', 'true');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('scrolling', 'no');
    iframe.style.height = '539px'; // JotForm's own starting height; setHeight corrects it

    body.appendChild(iframe);
    dialog.appendChild(close);
    dialog.appendChild(body);
    modal.appendChild(backdrop);
    modal.appendChild(dialog);
    document.body.appendChild(modal);

    entry.modal = modal;
    entry.iframe = iframe;
    entry.close = close;

    return entry;
  }

  function open(id) {
    var entry = build(id);
    if (!entry || !entry.modal) return;

    lastFocused = document.activeElement;
    openId = id;

    entry.modal.classList.add('is-open');
    document.documentElement.classList.add('jf-modal-open');

    if (entry.close) entry.close.focus();
  }

  function closeOpen() {
    if (!openId) return;

    var entry = forms[openId];
    if (entry && entry.modal) entry.modal.classList.remove('is-open');

    document.documentElement.classList.remove('jf-modal-open');
    openId = null;

    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  function onActivate(event) {
    var trigger = event.target.closest('[data-jotform-open]');
    if (trigger) {
      event.preventDefault();
      open(trigger.getAttribute('data-jotform-open'));
      return;
    }

    if (event.target.closest('[data-jotform-close]')) {
      event.preventDefault();
      closeOpen();
    }
  }

  function init() {
    var ids = claimTriggers();
    if (!ids.length) return;

    document.addEventListener('click', onActivate);

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && openId) {
        closeOpen();
        return;
      }

      // Keyboard equivalent of a click on the <a role="button"> triggers.
      if (event.key !== 'Enter' && event.key !== ' ') return;
      var trigger = event.target.closest && event.target.closest('[data-jotform-open]');
      if (trigger && !trigger.hasAttribute('href')) {
        event.preventDefault();
        open(trigger.getAttribute('data-jotform-open'));
      }
    });

    // JotForm reports its rendered height back over postMessage.
    window.addEventListener('message', function (event) {
      if (typeof event.data !== 'string' || !isJotformOrigin(event.origin)) return;

      var args = event.data.split(':');
      var entry = forms[args[2]];
      if (!entry || !entry.iframe) return;

      if (args[0] === 'setHeight') {
        entry.iframe.style.height = parseInt(args[1], 10) + 'px';
      } else if (args[0] === 'setMinHeight') {
        entry.iframe.style.minHeight = parseInt(args[1], 10) + 'px';
      } else if (args[0] === 'collapseErrorPage' && entry.iframe.clientHeight > window.innerHeight) {
        entry.iframe.style.height = window.innerHeight + 'px';
      }
    });

    /*
      Build the iframes once the page itself has finished loading, so the forms fetch during
      the time the visitor spends reading rather than competing with the page's own images.
      A click before this runs still works — open() builds on demand.
    */
    var start = function () {
      idle(function () {
        ids.forEach(build);
      });
    };

    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
